<template>
  <div class="map-filter-panel bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
    <!-- Panel title -->
    <div class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-medium text-gray-900">Nuclear Medicine Facilities </h2>
          <p class="mt-1 text-sm text-gray-600">Search and filter Nuclear Medicine facilities in Malaysia.</p>
        </div>
        <!-- Collapse button (mobile) -->
        <button @click="$emit('togglePanel')"
          class="md:hidden p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
          <i class="fas fa-times text-lg"></i>
        </button>
      </div>
    </div>

    <!-- Reset button (pinned at top) -->
    <div class="px-6 py-3 border-b border-gray-100 flex-shrink-0">
      <div class="space-y-3">
        <button @click="resetFilters"
          class="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm font-medium">
          <i class="fas fa-undo mr-2"></i>
          Reset All Filters
        </button>

        <!-- Search Now button (mobile only) -->
        <button @click="$emit('togglePanel')"
          class="md:hidden w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-sm font-medium">
          <i class="fas fa-search mr-2"></i>
          Search Now
        </button>
      </div>
    </div>

    <!-- Filter form -->
    <div class="flex-1 p-6 overflow-y-auto">
      <div class="space-y-6">

        <!-- Keyword search -->
        <div>
          <label for="keyword" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-search text-primary-600 mr-1"></i>
            Keyword Search
          </label>
          <div class="relative">
            <input id="keyword" v-model="localFilters.keyword" type="text"
              placeholder="Hospital/Institution name, service or radiopharmaceutical..."
              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm pl-10"
              @input="debouncedSearch" />
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i class="fas fa-search text-gray-400"></i>
            </div>
            <!-- Clear button -->
            <button v-if="localFilters.keyword" @click="clearKeyword"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- State filter -->
        <div>
          <label for="state" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-map-marker-alt text-secondary-600 mr-1"></i>
            State Filter
          </label>
          <select id="state" v-model="localFilters.state" :disabled="loadingOptions"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all">{{ loadingOptions ? 'Loading states...' : 'All States' }}</option>
            <option v-for="state in dynamicOptions.cities" :key="state" :value="state">
              {{ state }}
            </option>
          </select>
        </div>

        <!-- Service cascade filter -->
        <ServiceCascadeFilter :initial-filters="serviceFilters" @filters-changed="onServiceFiltersChanged" />

        <!-- Radiopharmaceutical filter -->
        <div>
          <label for="radiopharmaceutical" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-flask text-accent-600 mr-1"></i>
            Radiopharmaceuticals
          </label>
          <select id="radiopharmaceutical" v-model="localFilters.radiopharmaceutical"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all">All Radiopharmaceuticals</option>
            <optgroup v-for="(group, type) in groupedRadiopharmaceuticals" :key="type"
              :label="getRadiopharmaceuticalTypeLabel(type)">
              <option v-for="item in group" :key="item.value" :value="item.value">
                {{ item.label }}
              </option>
            </optgroup>
          </select>
        </div>

        <!-- Disease filter -->
        <div>
          <label for="disease" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-stethoscope text-warning-600 mr-1"></i>
            Type of Disease
          </label>
          <select id="disease" v-model="localFilters.disease" :disabled="loadingOptions"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="onDiseaseChange">
            <option value="all">{{ loadingOptions ? 'Loading diseases...' : 'All Disease Types' }}</option>
            <option v-for="disease in dynamicOptions.diseases" :key="disease.value" :value="disease.value"
              :title="disease.description">
              {{ disease.label }}
            </option>
          </select>
        </div>

        <!-- Disease Treatment filter (cascade) -->
        <div v-if="localFilters.disease !== 'all'">
          <label for="treatment" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-prescription-bottle-alt text-danger-600 mr-1"></i>
            Treatment Type
          </label>
          <select id="treatment" v-model="localFilters.treatment" :disabled="loadingTreatments"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all" v-if="loadingTreatments">Loading treatments...</option>
            <option value="all" v-else-if="availableTreatments.length > 0">All {{ getSelectedDiseaseLabel() }}
              Treatments</option>
            <option value="all" v-else>No treatments available</option>
            <option v-for="treatment in availableTreatments" :key="treatment.value" :value="treatment.value">
              {{ treatment.label }}
            </option>
          </select>
        </div>

        <!-- Research (Dynamic Dropdown) -->
        <div>
          <label for="research" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-flask text-primary-600 mr-1"></i>
            Research
          </label>
          <select id="research" v-model="localFilters.research" :disabled="loadingOptions"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all">{{ loadingOptions ? 'Loading research types...' : 'Choose Research Type' }}</option>
            <option v-for="research in dynamicOptions.researchOpportunities" :key="research.value"
              :value="research.value" :title="research.description">
              {{ research.label }}
            </option>
          </select>
        </div>

        <!-- Training (Dynamic Dropdown) -->
        <div>
          <label for="training" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-user-graduate text-accent-600 mr-1"></i>
            Training Opportunity
          </label>
          <select id="training" v-model="localFilters.training" :disabled="loadingOptions"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all">{{ loadingOptions ? 'Loading training opportunities...' : 'All Training Types' }}
            </option>
            <option v-for="training in dynamicOptions.trainingOpportunities" :key="training.value"
              :value="training.value" :title="training.description">
              {{ training.label }}
            </option>
          </select>
        </div>

        <!-- Collaboration Opportunities (Dynamic Dropdown) -->
        <div>
          <label for="collaboration" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-handshake text-secondary-600 mr-1"></i>
            Collaboration Opportunity
          </label>
          <select id="collaboration" v-model="localFilters.collaboration" :disabled="loadingOptions"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all">{{ loadingOptions ? 'Loading collaboration opportunities...' : 'All Collaboration Types'
              }}</option>
            <option v-for="collaboration in dynamicOptions.collaborationOpportunities" :key="collaboration.value"
              :value="collaboration.value" :title="collaboration.description">
              {{ collaboration.label }}
            </option>
          </select>
        </div>

        <!-- Accreditation (Dynamic Dropdown) -->
        <div>
          <label for="accreditation" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-certificate text-warning-600 mr-1"></i>
            Accreditation
          </label>
          <select id="accreditation" v-model="localFilters.accreditation" :disabled="loadingOptions"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all">{{ loadingOptions ? 'Loading accreditations...' : 'All Accreditation Types' }}</option>
            <option v-for="accreditation in dynamicOptions.accreditations" :key="accreditation.value"
              :value="accreditation.value" :title="accreditation.description">
              {{ accreditation.label }}
            </option>
          </select>
        </div>

        <!-- Specialization (Dynamic Dropdown) -->
        <div>
          <label for="specialization" class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-star text-danger-600 mr-1"></i>
            Specialization
          </label>
          <select id="specialization" v-model="localFilters.specialization" :disabled="loadingOptions"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @change="applyFilters">
            <option value="all">{{ loadingOptions ? 'Loading specializations...' : 'All Specialization Types' }}
            </option>
            <option v-for="specialization in dynamicOptions.specializations" :key="specialization.value"
              :value="specialization.value" :title="specialization.description">
              {{ specialization.label }}
            </option>
          </select>
        </div>

        <!-- Results statistics -->
        <div class="bg-primary-50 rounded-lg p-4 border border-primary-200">
          <div class="flex items-center">
            <div class="bg-primary-600 rounded-full p-2 mr-3">
              <i class="fas fa-chart-bar text-white text-sm"></i>
            </div>
            <div>
              <h3 class="text-sm font-medium text-primary-900">Search Results</h3>
              <p class="text-sm text-primary-700">
                Found <span class="font-bold">{{ resultStats.total }}</span> medical centers
                <span v-if="resultStats.filtered" class="text-xs text-primary-600">
                  (filtered from {{ resultStats.original }})
                </span>
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Show/hide panel button (mobile) -->
    <div class="md:hidden fixed bottom-6 left-6 z-50">
      <button @click="$emit('togglePanel')"
        class="bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 shadow-lg">
        <i class="fas fa-sliders-h"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, computed, onMounted } from 'vue'
import { debounce } from 'lodash'
import ServiceCascadeFilter from './ServiceCascadeFilter.vue'

export default {
  name: 'MapFilterPanel',
  components: {
    ServiceCascadeFilter
  },
  props: {
    filters: {
      type: Object,
      default: () => ({
        keyword: '',
        state: 'all',
        service: 'all'
      })
    },
    availableStates: {
      type: Array,
      default: () => []
    },
    availableServices: {
      type: Array,
      default: () => []
    },
    serviceStructure: {
      type: Object,
      default: () => ({})
    },
    availableRadiopharmaceuticals: {
      type: Array,
      default: () => []
    },
    availableDiseases: {
      type: Array,
      default: () => []
    },

    resultStats: {
      type: Object,
      default: () => ({
        total: 0,
        original: 0,
        filtered: false
      })
    }
  },
  emits: ['updateFilters', 'togglePanel'],
  setup(props, { emit }) {
    // Local filter state
    const localFilters = reactive({
      keyword: props.filters.keyword || '',
      state: props.filters.state || 'all',
      service: props.filters.service || 'all',
      radiopharmaceutical: props.filters.radiopharmaceutical || 'all',
      disease: props.filters.disease || 'all',
      treatment: props.filters.treatment || 'all',
      research: props.filters.research || 'all',
      training: props.filters.training || 'all',
      collaboration: props.filters.collaboration || 'all',
      accreditation: props.filters.accreditation || 'all',
      specialization: props.filters.specialization || 'all'
    })

    // Service cascade filter state
    const serviceFilters = reactive({
      category: 'all',
      serviceType: 'all',
      specificService: 'all'
    })

    // Computed properties for radiopharmaceuticals grouping
    const groupedRadiopharmaceuticals = computed(() => {
      const groups = {
        diagnostic: [],
        therapeutic: [],
        both: []
      }

      dynamicOptions.radiopharmaceuticals.forEach(item => {
        if (groups[item.type]) {
          groups[item.type].push(item)
        }
      })

      // Remove empty groups
      Object.keys(groups).forEach(key => {
        if (groups[key].length === 0) {
          delete groups[key]
        }
      })

      return groups
    })

    // Helper method for radiopharmaceutical type labels
    const getRadiopharmaceuticalTypeLabel = (type) => {
      const labels = {
        diagnostic: 'Diagnostic',
        therapeutic: 'Therapeutic',
        both: 'Diagnostic & Therapeutic'
      }
      return labels[type] || type
    }

    // Dynamic options loaded from API
    const dynamicOptions = reactive({
      countries: [],
      cities: [],
      radiopharmaceuticals: [],
      diseases: [],
      researchOpportunities: [],
      trainingOpportunities: [],
      collaborationOpportunities: [],
      accreditations: [],
      specializations: [],
    })

    const availableTreatments = ref([])
    const loadingOptions = ref(false)
    const loadingTreatments = ref(false)

    // Function to load all filter options from API
    const loadFilterOptions = async () => {
      loadingOptions.value = true
      try {
        const response = await fetch('/api/public/centers/filters?api_key=map-key-abc123')
        const data = await response.json()

        if (data.success) {
          Object.assign(dynamicOptions, {
            countries: data.data.countries || [],
            cities: data.data.cities || [],
            radiopharmaceuticals: data.data.radiopharmaceuticals || [],
            diseases: data.data.diseases || [],
            researchOpportunities: data.data.research_opportunities || [],
            trainingOpportunities: data.data.training_opportunities || [],
            collaborationOpportunities: data.data.collaboration_opportunities || [],
            accreditations: data.data.accreditations || [],
            specializations: data.data.specializations || [],
          })
        } else {
          console.error('Failed to load filter options:', data.message)
        }
      } catch (error) {
        console.error('Error loading filter options:', error)
      } finally {
        loadingOptions.value = false
      }
    }

    // Function to load treatments from API
    const loadTreatmentOptions = async (diseaseCode) => {
      if (!diseaseCode || diseaseCode === 'all') {
        availableTreatments.value = []
        return
      }

      loadingTreatments.value = true
      try {
        const response = await fetch(`/api/public/centers/treatments-by-disease?disease=${diseaseCode}&api_key=map-key-abc123`)
        const data = await response.json()

        if (data.success) {
          availableTreatments.value = data.data
        } else {
          console.error('Failed to load treatments:', data.message)
          availableTreatments.value = []
        }
      } catch (error) {
        console.error('Error loading treatments:', error)
        availableTreatments.value = []
      } finally {
        loadingTreatments.value = false
      }
    }

    // Debounced search
    const debouncedSearch = debounce(() => {
      applyFilters()
    }, 300)

    // Apply filters
    const applyFilters = () => {
      emit('updateFilters', { ...localFilters })
    }

    // Clear keyword
    const clearKeyword = () => {
      localFilters.keyword = ''
      applyFilters()
    }

    // Handle service cascade filter changes
    const onServiceFiltersChanged = (filters) => {
      Object.assign(serviceFilters, filters)

      // Update the main service filter based on cascade selection
      // Use the most specific service query available
      if (filters.serviceQuery) {
        localFilters.service = filters.serviceQuery
      } else {
        localFilters.service = 'all'
      }

      applyFilters()
    }

    // Reset filters
    const resetFilters = () => {
      localFilters.keyword = ''
      localFilters.state = 'all'
      localFilters.service = 'all'
      localFilters.radiopharmaceutical = 'all'
      localFilters.disease = 'all'
      localFilters.treatment = 'all'
      localFilters.research = 'all'
      localFilters.training = 'all'
      localFilters.collaboration = 'all'
      localFilters.accreditation = 'all'
      localFilters.specialization = 'all'

      // Reset service cascade filters
      serviceFilters.category = 'all'
      serviceFilters.serviceType = 'all'
      serviceFilters.specificService = 'all'

      // Reset treatments
      availableTreatments.value = []

      applyFilters()
    }

    // Watch for external filter changes
    watch(() => props.filters, (newFilters) => {
      Object.assign(localFilters, {
        keyword: newFilters.keyword || '',
        state: newFilters.state || 'all',
        service: newFilters.service || 'all',
        radiopharmaceutical: newFilters.radiopharmaceutical || 'all',
        disease: newFilters.disease || 'all',
        treatment: newFilters.treatment || 'all',
        research: newFilters.research || 'all',
        training: newFilters.training || 'all',
        collaboration: newFilters.collaboration || 'all',
        accreditation: newFilters.accreditation || 'all',
        specialization: newFilters.specialization || 'all'
      })
    }, { deep: true })

    // Watch for local filter non-input changes
    watch([
      () => localFilters.state,
      () => localFilters.service,
      () => localFilters.radiopharmaceutical,
      () => localFilters.disease,
      () => localFilters.treatment,
      () => localFilters.research,
      () => localFilters.training,
      () => localFilters.collaboration,
      () => localFilters.accreditation,
      () => localFilters.specialization
    ], () => {
      applyFilters()
    })

    // Load all filter options and initial treatment options
    onMounted(async () => {
      await loadFilterOptions()

      if (localFilters.disease && localFilters.disease !== 'all') {
        loadTreatmentOptions(localFilters.disease)
      }
    })

    // Handle disease change
    const onDiseaseChange = async () => {
      // Reset treatment when disease changes
      localFilters.treatment = 'all'

      // Load new treatment options for the selected disease
      await loadTreatmentOptions(localFilters.disease)

      // Immediately apply filters to update the map
      applyFilters()
    }

    // Get selected disease label
    const getSelectedDiseaseLabel = () => {
      const disease = dynamicOptions.diseases.find(d => d.value === localFilters.disease)
      return disease ? disease.label : 'Selected Disease'
    }

    return {
      localFilters,
      serviceFilters,
      dynamicOptions,
      groupedRadiopharmaceuticals,
      availableTreatments,
      loadingOptions,
      loadingTreatments,
      getRadiopharmaceuticalTypeLabel,
      debouncedSearch,
      applyFilters,
      clearKeyword,
      resetFilters,
      onServiceFiltersChanged,
      onDiseaseChange,
      getSelectedDiseaseLabel,
      loadTreatmentOptions,
      loadFilterOptions
    }
  }
}
</script>

<style scoped>
/* Scrollbar styles */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Mobile fixed position adjustment */
@media (max-width: 768px) {
  .map-filter-panel {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 40;
    border-radius: 0;
  }
}

/* Transition animation */
.transition-colors {
  transition-property: background-color, border-color, color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>